/**
 * Auth helpers and API client for login, session, password reset, invitations.
 */

const AUTH_STORAGE_KEY = "xlflow_access_token";
const API_BASE_DEFAULT = "/api";

export function getApiBase(): string {
  return (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_API_BASE_URL) || API_BASE_DEFAULT;
}

export function getAccessToken(): string | null {
  try {
    return window.localStorage.getItem(AUTH_STORAGE_KEY);
  } catch {
    return null;
  }
}

function setAccessToken(token: string): void {
  try {
    window.localStorage.setItem(AUTH_STORAGE_KEY, token);
  } catch {
    // ignore
  }
}

export function clearAccessToken(): void {
  try {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export type AuthUser = {
  id: string;
  email: string;
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  is_superadmin?: boolean;
  [key: string]: unknown;
};

type GetAuthHeadersOptions = { orgId?: string; json?: boolean };

export function getAuthHeaders(options?: GetAuthHeadersOptions): Record<string, string> {
  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (options?.orgId) {
    headers["X-Org-Id"] = options.orgId;
  }
  if (options?.json !== false) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
}

export async function extractApiErrorMessage(
  response: Response,
  defaultMessage: string
): Promise<string> {
  try {
    const text = await response.text();
    const json = text ? JSON.parse(text) : {};
    const detail = json.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0 && detail[0].msg) {
      return detail[0].msg;
    }
    return defaultMessage;
  } catch {
    return defaultMessage;
  }
}

/** Response from POST /auth/sync (or equivalent /me). */
type MeResponse = {
  user: AuthUser;
  memberships_with_permissions?: Array<unknown>;
};

export async function getMe(): Promise<MeResponse> {
  const base = getApiBase();
  const res = await fetch(`${base}/auth/sync`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  if (res.status === 401) {
    const err = new Error("Unauthorized") as Error & { status?: number };
    err.status = 401;
    throw err;
  }
  if (!res.ok) {
    const text = await res.text();
    let detail: string | undefined;
    try {
      const json = JSON.parse(text);
      detail = json.detail;
      if (Array.isArray(detail) && detail[0]?.msg) detail = detail[0].msg;
    } catch {
      detail = text || "Session invalide";
    }
    const err = new Error(detail || "Session invalide") as Error & { status?: number; code?: string };
    err.status = res.status;
    const code = res.headers.get("X-Error-Code");
    if (code) err.code = code;
    throw err;
  }
  const data = await res.json();
  return {
    user: data.user,
    memberships_with_permissions: data.memberships_with_permissions ?? [],
  };
}

export async function getCurrentUser(): Promise<AuthUser> {
  const me = await getMe();
  return me.user;
}

export async function loginLocal(email: string, password: string): Promise<AuthUser> {
  const base = getApiBase();
  const res = await fetch(`${base}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim(), password }),
  });
  if (!res.ok) {
    const defaultMsg =
      res.status >= 500
        ? "Service temporairement indisponible. Réessayez dans quelques instants."
        : "Identifiants invalides";
    const msg = await extractApiErrorMessage(res, defaultMsg);
    const err = new Error(msg) as Error & { status?: number; code?: string };
    err.status = res.status;
    const code = res.headers.get("X-Error-Code");
    if (code) err.code = code;
    throw err;
  }
  const data = await res.json();
  if (data.access_token) {
    setAccessToken(data.access_token);
  }
  return data.user;
}

export async function acceptInvitation(
  token: string,
  password: string,
  confirmPassword: string,
  phone: string,
  address_line1?: string | null,
  address_line2?: string | null,
  city?: string | null,
  postal_code?: string | null,
  province?: string | null,
  country?: string | null
): Promise<AuthUser> {
  const base = getApiBase();
  const res = await fetch(`${base}/auth/invitations/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      token,
      password,
      confirm_password: confirmPassword,
      phone,
      address_line1: address_line1 ?? undefined,
      address_line2: address_line2 ?? undefined,
      city: city ?? undefined,
      postal_code: postal_code ?? undefined,
      province: province ?? undefined,
      country: country ?? undefined,
    }),
  });
  if (!res.ok) {
    const msg = await extractApiErrorMessage(res, "Invitation invalide ou expirée");
    const err = new Error(msg) as Error & { code?: string };
    try {
      const json = await res.json();
      if (json.detail) err.code = json.detail;
    } catch {
      // ignore
    }
    throw err;
  }
  const data = await res.json();
  if (data.access_token) {
    setAccessToken(data.access_token);
  }
  return data.user;
}

export async function validateInvitationToken(token: string): Promise<{ status: string; email: string }> {
  const base = getApiBase();
  const res = await fetch(`${base}/auth/invitations/validate/${encodeURIComponent(token)}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const detail = data.detail || "token_invalid";
    const err = new Error(detail) as Error & { code?: string };
    err.code = detail;
    throw err;
  }
  return res.json();
}

export async function requestPasswordReset(email: string): Promise<void> {
  const base = getApiBase();
  const res = await fetch(`${base}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim() }),
  });
  if (!res.ok) {
    const msg = await extractApiErrorMessage(res, "Échec de la demande de réinitialisation");
    throw new Error(msg);
  }
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  const base = getApiBase();
  const res = await fetch(`${base}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  if (!res.ok) {
    const msg = await extractApiErrorMessage(res, "Lien invalide ou expiré.");
    throw new Error(msg);
  }
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const base = getApiBase();
  const res = await fetch(`${base}/auth/change-password`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!res.ok) {
    const msg = await extractApiErrorMessage(res, "Échec du changement de mot de passe");
    throw new Error(msg);
  }
}

export const BACKEND_INVENTORY_UNAVAILABLE_MSG =
  "Le module inventaire est temporairement indisponible.";
